<template>
  <div v-if="order" class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">คำสั่งซื้อ #{{ order.order_number }}</h1>
        <p class="text-gray-500 text-sm mt-1">{{ formatDateTime(order.created_at) }}</p>
      </div>
      <router-link to="/admin/orders" class="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors text-sm">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        กลับไปหน้ารายการ
      </router-link>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main Content -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Order Items Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h2 class="text-lg font-semibold text-gray-800">รายการสินค้า</h2>
            <span :class="getStatusClasses(order.status)">
              {{ getStatusText(order.status) }}
            </span>
          </div>
          
          <!-- Mobile Order Items -->
          <div class="block sm:hidden divide-y divide-gray-100">
            <div v-for="item in order.items" :key="item.id" class="p-4">
              <p class="font-medium text-gray-800">{{ item.product_name }}</p>
              <p v-if="item.variant_info" class="text-sm text-gray-500 mt-0.5">{{ item.variant_info }}</p>
              <div class="flex justify-between mt-3 text-sm">
                <span class="text-gray-500">{{ formatPrice(item.unit_price) }} × {{ item.quantity }}</span>
                <span class="font-semibold text-gray-800">{{ formatPrice(item.total_price) }}</span>
              </div>
            </div>
          </div>

          <!-- Desktop Order Items Table -->
          <div class="hidden sm:block">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50 text-left text-sm text-gray-600">
                  <th class="px-6 py-3 font-medium">สินค้า</th>
                  <th class="px-6 py-3 font-medium">ราคา</th>
                  <th class="px-6 py-3 font-medium text-center">จำนวน</th>
                  <th class="px-6 py-3 font-medium text-right">รวม</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="item in order.items" :key="item.id" class="hover:bg-gray-50/50 transition-colors">
                  <td class="px-6 py-4">
                    <p class="font-medium text-gray-800">{{ item.product_name }}</p>
                    <p v-if="item.variant_info" class="text-sm text-gray-500 mt-0.5">{{ item.variant_info }}</p>
                  </td>
                  <td class="px-6 py-4 text-gray-600">{{ formatPrice(item.unit_price) }}</td>
                  <td class="px-6 py-4 text-center text-gray-600">{{ item.quantity }}</td>
                  <td class="px-6 py-4 text-right font-semibold text-gray-800">{{ formatPrice(item.total_price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Order Summary -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <div class="flex justify-between items-center">
              <span class="text-gray-600">ยอดรวมสินค้า</span>
              <span class="text-xl font-bold text-primary">{{ formatPrice(order.total_amount) }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Slip Card -->
        <div v-if="order.payment_slip" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800">หลักฐานการชำระเงิน</h2>
          </div>
          <div class="p-6">
            <div class="flex flex-col md:flex-row gap-6">
              <!-- Slip Image -->
              <div class="shrink-0">
                <a :href="getImageUrl(order.payment_slip.image_path)" target="_blank" class="block group">
                  <div class="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                    <img 
                      :src="getImageUrl(order.payment_slip.image_path)" 
                      class="max-w-[240px] max-h-[360px] w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      alt="สลิปการโอนเงิน"
                    >
                    <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
              
              <!-- Slip Details -->
              <div class="flex-1 space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div class="bg-gray-50 rounded-lg p-4">
                    <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">วันที่โอน</p>
                    <p class="font-semibold text-gray-800">{{ formatDate(order.payment_slip.transfer_date) }}</p>
                  </div>
                  <div class="bg-gray-50 rounded-lg p-4">
                    <p class="text-xs text-gray-500 uppercase tracking-wide mb-1">เวลาที่โอน</p>
                    <p class="font-semibold text-gray-800">{{ order.payment_slip.transfer_time }}</p>
                  </div>
                </div>
                
                <!-- Approve Button -->
                <div v-if="order.status === 'pending_verification'" class="pt-2">
                  <button 
                    @click="approvePayment" 
                    class="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    ยืนยันการชำระเงิน
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Customer Info Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800">ข้อมูลลูกค้า</h2>
          </div>
          <div class="p-6 space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wide">ชื่อ</p>
                <p class="font-medium text-gray-800">{{ order.customer_name }}</p>
              </div>
            </div>
            
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </div>
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wide">โทร</p>
                <p class="font-medium text-gray-800">{{ order.phone }}</p>
              </div>
            </div>
            
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wide">ที่อยู่จัดส่ง</p>
                <p class="text-gray-700 text-sm leading-relaxed">{{ order.address }}</p>
              </div>
            </div>

            <!-- Notes -->
            <div v-if="order.notes" class="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
              <div class="flex items-start gap-2">
                <svg class="w-4 h-4 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                </svg>
                <div>
                  <p class="text-xs font-medium text-amber-700 uppercase tracking-wide">หมายเหตุ</p>
                  <p class="text-amber-800 text-sm mt-1">{{ order.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Cancel Reason -->
            <div v-if="order.status === 'cancelled' && order.cancel_reason" class="mt-4 p-4 bg-red-50 rounded-lg border border-red-100">
              <div class="flex items-start gap-2">
                <svg class="w-4 h-4 text-red-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="text-xs font-medium text-red-700 uppercase tracking-wide">เหตุผลที่ยกเลิก</p>
                  <p class="text-red-800 text-sm mt-1">{{ order.cancel_reason }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cancel Order Card -->
        <div v-if="canCancelOrder" class="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-red-100 bg-red-50">
            <h2 class="text-lg font-semibold text-red-700">ยกเลิกคำสั่งซื้อ</h2>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-600 mb-4">หากต้องการยกเลิกคำสั่งซื้อนี้ กรุณาระบุเหตุผล</p>
            <button 
              @click="showCancelModal = true" 
              class="w-full inline-flex items-center justify-center gap-2 bg-red-600 text-white py-2.5 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              ยกเลิกคำสั่งซื้อ
            </button>
          </div>
        </div>

        <!-- Receipt Card -->
        <div v-if="canShowReceipt" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800">ใบเสร็จ / ใบกำกับ</h2>
          </div>
          <div class="p-6 space-y-3">
            <button 
              @click="showReceiptModal = true" 
              class="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-2.5 rounded-lg hover:bg-primary-dark transition-colors font-medium"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              ดูใบเสร็จ
            </button>
            <button 
              @click="downloadReceiptPdf" 
              :disabled="isDownloading"
              class="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              {{ isDownloading ? 'กำลังดาวน์โหลด...' : 'ดาวน์โหลด PDF' }}
            </button>
          </div>
        </div>

        <!-- Shipping Card -->
        <div v-if="canShowShipping" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800">การจัดส่ง</h2>
          </div>
          <div class="p-6 space-y-4">
            <!-- Existing Parcel Image -->
            <div v-if="order.shipping && (order.shipping.parcel_image_url || order.shipping.parcel_image_path)" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">รูปพัสดุที่อัปโหลดแล้ว</label>
              <a :href="getImageUrl(order.shipping.parcel_image_url || order.shipping.parcel_image_path)" target="_blank" class="block">
                <img 
                  :src="getImageUrl(order.shipping.parcel_image_url || order.shipping.parcel_image_path)" 
                  class="w-28 h-auto rounded-lg border border-gray-200 hover:opacity-90 transition-opacity"
                  alt="รูปพัสดุ"
                >
              </a>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">เลขพัสดุ (Tracking Number)</label>
              <input 
                v-model="trackingNumber" 
                type="text" 
                class="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="กรอกเลขพัสดุ"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">ขนส่ง (Carrier)</label>
              <input 
                v-model="carrier" 
                type="text" 
                class="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="เช่น Kerry, EMS, Flash"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">รูปพัสดุ</label>
              <input 
                type="file" 
                accept="image/*"
                @change="handleParcelImageUpload"
                class="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              >
              <div v-if="parcelImagePreview" class="mt-3">
                <img :src="parcelImagePreview" class="w-24 h-auto rounded-lg border border-gray-200">
              </div>
            </div>
            
            <button 
              @click="updateShipping" 
              :disabled="!trackingNumber || isSubmitting"
              class="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-2.5 rounded-lg hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="isSubmitting" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              {{ isSubmitting ? 'กำลังบันทึก...' : 'บันทึกการจัดส่ง' }}
            </button>
          </div>
        </div>
      </div>
    </div>


    <!-- Cancel Order Modal -->
    <div v-if="showCancelModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="closeCancelModal"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-modal-in">
        <!-- Header -->
        <div class="px-6 py-5 bg-gradient-to-r from-red-50 to-red-100 border-b border-red-100">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div>
              <h2 class="text-lg font-bold text-red-800">ยกเลิกคำสั่งซื้อ</h2>
              <p class="text-sm text-red-600">#{{ order.order_number }}</p>
            </div>
          </div>
        </div>
        
        <!-- Body -->
        <div class="p-6">
          <div class="mb-5">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              เหตุผลที่ยกเลิก <span class="text-red-500">*</span>
            </label>
            <textarea 
              v-model="cancelReason" 
              rows="3" 
              class="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all resize-none"
              placeholder="กรุณาระบุเหตุผลที่ยกเลิก เช่น ลูกค้าขอยกเลิก, สินค้าหมด, ฯลฯ"
            ></textarea>
            <p v-if="cancelReasonError" class="text-red-500 text-sm mt-1.5 flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {{ cancelReasonError }}
            </p>
          </div>
          
          <div class="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-amber-800 text-sm">
                หากคำสั่งซื้อนี้มีการชำระเงินแล้ว ระบบจะคืนสต๊อกสินค้าให้อัตโนมัติ
              </p>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t flex gap-3 justify-end">
          <button 
            @click="closeCancelModal" 
            class="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            ยกเลิก
          </button>
          <button 
            @click="confirmCancelOrder" 
            :disabled="isCancelling"
            class="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium disabled:opacity-50 transition-colors inline-flex items-center gap-2"
          >
            <svg v-if="isCancelling" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isCancelling ? 'กำลังดำเนินการ...' : 'ยืนยันยกเลิก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Receipt Modal -->
    <div v-if="showReceiptModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-modal-in">
        <div class="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 class="text-xl font-bold text-gray-800">ใบเสร็จรับเงิน</h2>
          <button @click="showReceiptModal = false" class="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors">
            <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      
        <div v-if="receiptData" class="receipt-content p-6 sm:p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
          <!-- Shop Header -->
          <div class="text-center pb-6 mb-6 border-b-2 border-gray-200">
            <h3 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{{ receiptData.shop_info?.name || 'pruksamarket' }}</h3>
            <p class="text-gray-500 text-lg">ใบเสร็จรับเงิน / ใบกำกับสินค้า</p>
          </div>
        
          <!-- Order Info -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            <div class="space-y-3">
              <div class="flex">
                <span class="text-gray-500 w-36">เลขที่คำสั่งซื้อ:</span>
                <span class="font-semibold text-gray-800">{{ receiptData.order_details?.order_number }}</span>
              </div>
              <div class="flex">
                <span class="text-gray-500 w-36">วันที่ออกเอกสาร:</span>
                <span class="text-gray-800">{{ receiptData.date }}</span>
              </div>
            </div>
            <div class="space-y-3">
              <div class="flex">
                <span class="text-gray-500 w-24">ลูกค้า:</span>
                <span class="font-semibold text-gray-800">{{ receiptData.order_details?.customer_name }}</span>
              </div>
              <div class="flex">
                <span class="text-gray-500 w-24">โทรศัพท์:</span>
                <span class="text-gray-800">{{ receiptData.order_details?.phone || '-' }}</span>
              </div>
              <div class="flex">
                <span class="text-gray-500 w-24">ที่อยู่:</span>
                <span class="text-gray-800">{{ receiptData.order_details?.address || '-' }}</span>
              </div>
            </div>
          </div>
        
          <!-- Items Table -->
          <div class="mb-8 overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-100">
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b-2 border-gray-300 rounded-tl-lg">รายการสินค้า</th>
                  <th class="text-right py-3 px-4 font-semibold text-gray-700 border-b-2 border-gray-300">ราคา/หน่วย</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b-2 border-gray-300">จำนวน</th>
                  <th class="text-right py-3 px-4 font-semibold text-gray-700 border-b-2 border-gray-300 rounded-tr-lg">รวม</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in receiptData.order_details?.items" :key="index" class="border-b border-gray-200 hover:bg-gray-50">
                  <td class="py-3 px-4">
                    <span class="text-gray-800">{{ item.name }}</span>
                    <span v-if="item.variant" class="text-gray-500 text-sm ml-1">({{ item.variant }})</span>
                  </td>
                  <td class="py-3 px-4 text-right text-gray-700">{{ formatPrice(item.price) }}</td>
                  <td class="py-3 px-4 text-center text-gray-700">{{ item.quantity }}</td>
                  <td class="py-3 px-4 text-right font-medium text-gray-800">{{ formatPrice(item.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        
          <!-- Totals -->
          <div class="flex justify-end mb-8">
            <div class="w-72 bg-gray-50 rounded-xl p-4">
              <div class="flex justify-between py-2 text-gray-600">
                <span>ยอดรวมสินค้า:</span>
                <span>{{ formatPrice(receiptData.order_details?.subtotal) }}</span>
              </div>
              <div class="flex justify-between py-2 text-gray-600">
                <span>ค่าจัดส่ง:</span>
                <span>{{ formatPrice(receiptData.order_details?.shipping_fee || 0) }}</span>
              </div>
              <div class="flex justify-between py-3 mt-2 border-t-2 border-gray-300 text-xl font-bold text-gray-800">
                <span>ยอดรวมทั้งสิ้น:</span>
                <span class="text-primary">{{ formatPrice(receiptData.order_details?.total_amount) }}</span>
              </div>
            </div>
          </div>
        
          <!-- Note -->
          <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p class="text-amber-700 text-sm">
              <span class="font-semibold">หมายเหตุ:</span> เอกสารนี้ไม่ใช่ใบกำกับภาษี ไม่มีการคิด VAT และไม่มีเลขประจำตัวผู้เสียภาษี
            </p>
          </div>
        
          <!-- Footer -->
          <div class="text-center text-gray-500 pt-6 border-t border-gray-200">
            <p class="text-lg">ขอบคุณที่ใช้บริการ</p>
            <p class="font-semibold text-gray-700">{{ receiptData.shop_info?.name || 'pruksamarket' }}</p>
          </div>
        </div>
      
        <div v-else class="p-8 text-center text-gray-500">
          <svg class="animate-spin h-10 w-10 mx-auto mb-4 text-primary" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p>กำลังโหลดข้อมูล...</p>
        </div>
      
        <!-- Modal Footer -->
        <div class="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button 
            @click="downloadReceiptPdf" 
            :disabled="isDownloading"
            class="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl hover:bg-green-700 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            {{ isDownloading ? 'กำลังดาวน์โหลด...' : 'ดาวน์โหลด PDF' }}
          </button>
          <button @click="showReceiptModal = false" class="bg-gray-200 text-gray-700 px-5 py-2.5 rounded-xl hover:bg-gray-300 text-sm font-medium transition-colors">
            ปิด
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';
import { getImageUrl } from '../../utils/imageUrl';
import { getOrderStatusLabel } from '../../utils/orderStatus';

const route = useRoute();
const { showSuccess, showError, showWarning, confirmAction, handleApiError } = useNotification();
const order = ref(null);
const trackingNumber = ref('');
const carrier = ref('');
const parcelImage = ref(null);
const parcelImagePreview = ref('');
const isSubmitting = ref(false);
const showReceiptModal = ref(false);
const receiptData = ref(null);
const isDownloading = ref(false);

// Cancel order state
const showCancelModal = ref(false);
const cancelReason = ref('');
const cancelReasonError = ref('');
const isCancelling = ref(false);

// Computed properties
const canCancelOrder = computed(() => {
  return order.value && !['cancelled', 'shipped', 'delivered'].includes(order.value.status);
});

const canShowReceipt = computed(() => {
  return order.value && ['paid', 'shipped'].includes(order.value.status);
});

const canShowShipping = computed(() => {
  return order.value && ['paid', 'shipped'].includes(order.value.status);
});

// Handle parcel image upload
const handleParcelImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith('image/')) {
      showError('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }
    parcelImage.value = file;
    parcelImagePreview.value = URL.createObjectURL(file);
  }
};

onMounted(async () => {
  fetchOrder();
});

const fetchOrder = async () => {
  try {
    const response = await api.get(`/admin/orders/${route.params.id}`);
    order.value = response.data.data;
    trackingNumber.value = order.value.shipping?.tracking_number || '';
    carrier.value = order.value.shipping?.company_name || '';
  } catch (error) {
    showError('ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้');
  }
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('th-TH', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusClasses = (status) => {
  const classes = {
    'pending_payment': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200',
    'pending_verification': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-orange-100 text-orange-800 border border-orange-200',
    'paid': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-800 border border-green-200',
    'processing': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200',
    'shipped': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-purple-100 text-purple-800 border border-purple-200',
    'delivered': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200',
    'cancelled': 'px-3 py-1.5 rounded-full text-sm font-semibold bg-red-100 text-red-800 border border-red-200'
  };
  return classes[status] || 'px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-800 border border-gray-200';
};

const getStatusText = (status) => getOrderStatusLabel(status);

const approvePayment = async () => {
  const confirmed = await confirmAction('ยืนยันการชำระเงิน?', 'คุณต้องการยืนยันการชำระเงินนี้ใช่หรือไม่', 'ยืนยัน', 'ยกเลิก');
  if (confirmed) {
    try {
      await api.post(`/admin/orders/${order.value.id}/approve`);
      showSuccess('ยืนยันการชำระเงินเรียบร้อยแล้ว');
      fetchOrder();
    } catch (error) {
      handleApiError(error);
    }
  }
};

// Cancel order functions
const closeCancelModal = () => {
  showCancelModal.value = false;
  cancelReason.value = '';
  cancelReasonError.value = '';
};

const confirmCancelOrder = async () => {
  if (!cancelReason.value.trim()) {
    cancelReasonError.value = 'กรุณาระบุเหตุผลที่ยกเลิก';
    return;
  }
  
  isCancelling.value = true;
  cancelReasonError.value = '';
  
  try {
    await api.put(`/admin/orders/${order.value.id}/status`, {
      status: 'cancelled',
      cancel_reason: cancelReason.value.trim()
    });
    showSuccess('ยกเลิกคำสั่งซื้อเรียบร้อยแล้ว');
    closeCancelModal();
    fetchOrder();
  } catch (error) {
    handleApiError(error);
  } finally {
    isCancelling.value = false;
  }
};

const updateShipping = async () => {
  if (!trackingNumber.value) {
    showWarning('กรุณากรอกเลขพัสดุ');
    return;
  }

  isSubmitting.value = true;

  try {
    const formData = new FormData();
    formData.append('tracking_number', trackingNumber.value);
    formData.append('company_name', carrier.value || 'ไม่ระบุ');
    
    if (parcelImage.value) {
      formData.append('parcel_image', parcelImage.value);
    }

    await api.post(`/admin/orders/${order.value.id}/shipping`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    showSuccess('บันทึกข้อมูลการจัดส่งเรียบร้อยแล้ว');
    parcelImage.value = null;
    parcelImagePreview.value = '';
    fetchOrder();
  } catch (error) {
    handleApiError(error);
  } finally {
    isSubmitting.value = false;
  }
};

// Receipt functions
const fetchReceipt = async () => {
  if (!order.value) return;
  try {
    const response = await api.get(`/admin/orders/${order.value.id}/receipt`);
    receiptData.value = response.data.data;
  } catch (error) {
    showError('ไม่สามารถโหลดข้อมูลใบเสร็จได้');
  }
};

const downloadReceiptPdf = async () => {
  if (!order.value) return;
  isDownloading.value = true;
  try {
    const response = await api.get(`/admin/orders/${order.value.id}/receipt/pdf`, {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${order.value.order_number}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    showSuccess('ดาวน์โหลดใบเสร็จเรียบร้อยแล้ว');
  } catch (error) {
    handleApiError(error);
  } finally {
    isDownloading.value = false;
  }
};

// Watch for modal open to fetch receipt data
watch(showReceiptModal, (newVal) => {
  if (newVal && !receiptData.value) {
    fetchReceipt();
  }
});
</script>

<style scoped>
.receipt-content {
  font-family: 'Sarabun', sans-serif;
  line-height: 1.6;
}

@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-in {
  animation: modal-in 0.2s ease-out;
}
</style>
